import { resolveCountyFromAddress } from './fetchApi'

function normalizeAddressPart(value) {
    const normalized = `${value ?? ''}`.trim()
    if (!normalized) {
        return ''
    }

    return normalized.replace(/\s+/g, ' ')
}

function normalizeZip(zip) {
    const digits = `${zip ?? ''}`.replace(/\D/g, '').split('-')[0]
    if (!digits) {
        return ''
    }

    if (digits.length === 9) {
        return digits.slice(0, 5)
    }

    if (digits.length === 6) {
        return digits.slice(-5)
    }

    if (digits.length > 5) {
        return digits.slice(0, 5)
    }

    return digits
}

export function contactAddressFingerprint(contact) {
    const street = normalizeAddressPart(contact?.address_1)
    const city = normalizeAddressPart(contact?.city)
    const state = `${contact?.state ?? ''}`.trim().toUpperCase()
    const zip = normalizeZip(contact?.zip)

    return [street, city, state, zip].join('|')
}

export function hasContactAddressChanged(originalContact, formContact) {
    return contactAddressFingerprint(originalContact) !== contactAddressFingerprint(formContact)
}

export const patientAddressFingerprint = contactAddressFingerprint
export const hasPatientAddressChanged = hasContactAddressChanged

export function buildEntityFormContact(data) {
    return {
        address_1: `${data?.address_line_1 ?? ''}`.trim(),
        address_2: `${data?.address_line_2 ?? ''}`.trim() !== '' ? `${data?.address_line_2}`.trim() : null,
        city: data?.city ?? '',
        state: data?.state ?? '',
        zip: data?.zip_code ?? '',
    }
}

export function buildPatientFormContact(formData) {
    return {
        address_1: `${formData?.address_line_1 ?? ''}`.trim(),
        address_2: `${formData?.address_line_2 ?? ''}`.trim() !== '' ? `${formData?.address_line_2}`.trim() : null,
        city: formData?.location_city ?? '',
        state: formData?.state ?? '',
        zip: formData?.location_zip_code ?? '',
    }
}

export function buildCountyOptionFromResolved(resolved, countiesOptions) {
    if (!resolved?.county_id) {
        return null
    }

    const allCounties = typeof countiesOptions === 'function' ? countiesOptions() : countiesOptions
    const match = allCounties.find((item) => item.value == resolved.county_id)

    if (match) {
        return match
    }

    return {
        value: resolved.county_id,
        label: resolved.county_name ?? '',
        state: resolved.state,
    }
}

export function resolveSelectedCountyLabel(selectedCountyId, countiesOptions) {
    if (!selectedCountyId) {
        return ''
    }

    const allCounties = typeof countiesOptions === 'function' ? countiesOptions() : countiesOptions
    const match = allCounties.find((item) => item.value == selectedCountyId)

    return match?.label ?? ''
}

export function hasMinimumAddressForCountyResolve(formContact) {
    return Boolean(
        normalizeAddressPart(formContact?.address_1) &&
        normalizeAddressPart(formContact?.city) &&
        `${formContact?.state ?? ''}`.trim() &&
        normalizeZip(formContact?.zip),
    )
}

export async function resolveCountyBeforeSave({
    token,
    originalContact,
    formContact,
    selectedCountyId = null,
    selectedCountyLabel = '',
    confirmCountyChoice,
    countiesOptions,
    onSuggestedCounty,
}) {
    const selectedId = selectedCountyId ?? null
    const selectedLabel = selectedCountyLabel || resolveSelectedCountyLabel(selectedId, countiesOptions)

    // Always verify county on save when address fields are complete, even if the user
    // changed county/ZIP/address and changed it back to the original values.
    if (!hasMinimumAddressForCountyResolve(formContact)) {
        return {
            countyId: selectedId,
            countyLabel: selectedLabel,
            cancelled: false,
        }
    }

    const countyResolution = await resolveCountyFromAddress(token, {
        address_1: formContact.address_1,
        city: formContact.city,
        state: formContact.state,
        zip: formContact.zip,
        county_id: selectedId,
    })

    if (countyResolution?.errorMessage) {
        return { error: countyResolution }
    }

    if (!countyResolution?.mismatch || !countyResolution?.resolved) {
        return {
            countyId: selectedId,
            countyLabel: selectedLabel,
            cancelled: false,
        }
    }

    const choice = await confirmCountyChoice({
        selectedCountyName: countyResolution?.selected_county?.county_name ?? selectedLabel,
        suggestedCountyName: countyResolution.resolved.county_name,
        source: countyResolution.resolved.source,
    })

    if (choice === null) {
        return { cancelled: true }
    }

    if (choice === 'suggested') {
        const suggestedCounty = buildCountyOptionFromResolved(countyResolution.resolved, countiesOptions)
        if (typeof onSuggestedCounty === 'function') {
            onSuggestedCounty(suggestedCounty)
        }

        return {
            countyId: suggestedCounty?.value ?? countyResolution.resolved.county_id,
            countyLabel: suggestedCounty?.label ?? countyResolution.resolved.county_name,
            cancelled: false,
        }
    }

    return {
        countyId: selectedId,
        countyLabel: selectedLabel,
        cancelled: false,
    }
}

export function formatCountySourceLabel(source) {
    if (source === 'census') {
        return 'Census street lookup'
    }

    if (source === 'zip') {
        return 'ZIP lookup table'
    }

    return 'address lookup'
}
